import {
  Breadcrumb,
  Button,
  Input,
  message,
  PageHeader,
  Select,
  Form,
  Row,
  Col,
  Tag,
  Table,
  Spin,
  DatePicker,
  Divider,
  Typography,
} from "antd";

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import Page from "_components/Page";
import { ApplicationDocumentService } from "_services";
import TabMenu from "./TabMenu";
import { TiepDocumentType, tiepApplicationStatuses } from "_helpers/enums";

const { Option } = Select;

function Summary({ history, match }) {
  const { id: companyId } = match.params;

  const [isLoading, setIsLoading] = useState(false);
  const [stockType, setStockType] = useState({});
  const [companyDocuments, setCompanyDocuments] = useState([]);
  const [form] = Form.useForm();
  const { Option } = Select;

  useEffect(() => {
    setIsLoading(true);
    // Use the application ID from URL params to get document details
    ApplicationDocumentService
      .getById(companyId)
      .then((x) => {
        let data = x.content;
        setStockType(x.content);
        onFill(data);
        
        // Fetch company documents if company ID exists
        if (data.company?.id) {
          ApplicationDocumentService.getSubDocumentByCompanyId(data.company.id)
            .then((response) => {
              const documents = response.content || [];
              setCompanyDocuments(Array.isArray(documents) ? documents : [documents]);
              setIsLoading(false);
            })
            .catch((error) => {
              console.error("Error fetching company documents:", error);
              setCompanyDocuments([]);
              setIsLoading(false);
            });
        } else {
          setIsLoading(false);
        }
      })
      .catch((errorInfo) => {
        setIsLoading(false);
        message.error(`Error loading document: ${errorInfo}`);
      });
  }, [companyId]);


  const onFill = (data) => {
    // Map application document data to form fields
    const formData = {
      // Application details
      applicationDocumentType: data.applicationDocumentType,
      applicationStatus: data.applicationStatus,
      
      // Company information
      company: data.company?.name || '',
      companyAddress: data.companyAddress?.address || '',
      
      // Workshop information
      workshop: data.workshop?.name || '',
      workshopAddress: data.addressOfWorkShop?.address || '',
      
      // Application details
      applicationDate: data.applicationDate ? moment(data.applicationDate) : null,
      approvalNo: data.approvalNo,
      customRefNo: data.customRefNo,
      
      // Exporter information
      cumExporter: data.cumExporter,
      indirectExporter: data.indirectExporter,
      indirectExporterCompanies: data.indirectExporterCompanies?.map(e => e.name).join(', ') || '',
      exporter: data.exporter?.name || '',
      exporterAddress: data.exporterAddress?.address || '',
      
      // Guarantee requests
      requestBankGuarantee: data.requestBankGuarantee,
      requestBankAndExporterGuarantee: data.requestBankAndExporterGuarantee,
      requestExporterGuarantee: data.requestExporterGuarantee,
      
      // Approval guarantees
      approvedBankGuarantee: data.approvedBankGuarantee,
      approvedBankAndExporterGuarantee: data.approvedBankAndExporterGuarantee,
      approvedExporterGuarantee: data.approvedExporterGuarantee,
      
      // Authorization
      authorisedBy: data.authorisedBy?.fullName || data.authorisedBy?.username || '',
      designation: data.designation,
      
      // Approval details
      approvedBy: data.approvedBy?.fullName || data.approvedBy?.username || '',
      approvedDate: data.approvedDate ? moment(data.approvedDate) : null,
      
      // Dates
      validFrom: data.validFrom ? moment(data.validFrom) : null,
      validTo: data.validTo ? moment(data.validTo) : null,
      
      // Comments
      comment: data.comment,
    };
    form.setFieldsValue(formData);
  };

  const onCancel = () => {
    history.push("/documents");
  };

  const getActionButtons = () => {
    let buttons = [];
    
    buttons.push(
      <Button key="cancel" onClick={onCancel} style={{ marginRight: 8 }}>
        Back to List
      </Button>
    );
    
    buttons.push(
      <Button 
        key="download" 
        type="primary" 
        onClick={() => {
          if (stockType.id) {
            // For main document, keep existing download
            ApplicationDocumentService.downloadDocument(stockType.id)
              .then(() => {
                message.success("Download started!");
              })
              .catch((error) => {
                message.error("Download failed: " + error.message);
              });
          }
        }}
      >
        Download Document
      </Button>
    );
    
    return buttons;
  };


  return (
    <div>
      <PageHeader
        title={
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to="/dashboard">Dashboard</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link to="/documents">Documents</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <span>Application Summary</span>
              {stockType?.applicationStatus && (
                <Tag
                  color={
                    stockType.applicationStatus === "APPROVAL"
                      ? "green"
                      : stockType.applicationStatus === "CLOSURE"
                      ? "red"
                      : stockType.applicationStatus === "APPLICATION"
                      ? "blue"
                      : stockType.applicationStatus === "EXTENSIONS"
                      ? "purple"
                      : "orange"
                  }
                  style={{ marginLeft: 8 }}
                >
                  {stockType.applicationStatus}
                </Tag>
              )}
            </Breadcrumb.Item>
          </Breadcrumb>
        }
        //Add buttons in the page header
        extra={getActionButtons()}
      >
        <Page inner>
          <Spin tip="Loading..." spinning={isLoading}>
            <TabMenu menu="summary" id={companyId} />
            <Form
              form={form}
              name="form-create"
              layout="vertical"
              requiredMark={false}
            >
              <Row gutter={24}>
                <Col span={24}>
                  <Divider orientation="left" style={{ marginTop: 0, marginBottom: 16 }}>
                    <Typography.Title level={5} style={{ margin: 0 }}>
                      Application Information
                    </Typography.Title>
                  </Divider>
                </Col>

                <Col span={6}>
                  <Form.Item name="applicationDocumentType" label="Application Document Type">
                    <Select placeholder="Select Document Type" disabled>
                      {TiepDocumentType.map((type) => (
                        <Option key={type.value} value={type.value}>
                          {type.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                <Col span={6}>
                  <Form.Item name="applicationStatus" label="Application Status">
                    <Select placeholder="Select Application Status" disabled>
                      {tiepApplicationStatuses.map((status) => (
                        <Option key={status.value} value={status.value}>
                          {status.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                <Col span={6}>
                  <Form.Item name="applicationDate" label="Application Date">
                    <DatePicker
                      placeholder="Application Date"
                      format="YYYY-MM-DD"
                      style={{ width: "100%" }}
                      disabled
                    />
                  </Form.Item>
                </Col>

                <Col span={6}>
                  <Form.Item name="approvalNo" label="Approval Number">
                    <Input placeholder="Approval Number" readOnly />
                  </Form.Item>
                </Col>

                <Col span={24}>
                  <Divider orientation="left" style={{ marginTop: 24, marginBottom: 16 }}>
                    <Typography.Title level={5} style={{ margin: 0 }}>
                      Company Information
                    </Typography.Title>
                  </Divider>
                </Col>

                <Col span={8}>
                  <Form.Item name="company" label="Company">
                    <Input placeholder="Company" readOnly />
                  </Form.Item>
                </Col>

                <Col span={16}>
                  <Form.Item name="companyAddress" label="Company Address">
                    <Input placeholder="Company Address" readOnly />
                  </Form.Item>
                </Col>

                <Col span={24}>
                  <Divider orientation="left" style={{ marginTop: 24, marginBottom: 16 }}>
                    <Typography.Title level={5} style={{ margin: 0 }}>
                      Workshop Information
                    </Typography.Title>
                  </Divider>
                </Col>

                <Col span={8}>
                  <Form.Item name="workshop" label="Workshop">
                    <Input placeholder="Workshop" readOnly />
                  </Form.Item>
                </Col>

                <Col span={16}>
                  <Form.Item name="workshopAddress" label="Workshop Address">
                    <Input placeholder="Workshop Address" readOnly />
                  </Form.Item>
                </Col>

                <Col span={24}>
                  <Divider orientation="left" style={{ marginTop: 24, marginBottom: 16 }}>
                    <Typography.Title level={5} style={{ margin: 0 }}>
                      Exporter Information
                    </Typography.Title>
                  </Divider>
                </Col>

                <Col span={6}>
                  <Form.Item name="customRefNo" label="Custom Ref Number">
                    <Input placeholder="Custom Ref Number" readOnly />
                  </Form.Item>
                </Col>

                <Col span={6}>
                  <Form.Item name="cumExporter" label="Cum Exporter">
                    <Select placeholder="Cum Exporter" disabled>
                      <Option value={true}>Yes</Option>
                      <Option value={false}>No</Option>
                    </Select>
                  </Form.Item>
                </Col>

                <Col span={6}>
                  <Form.Item name="indirectExporter" label="Indirect Exporter">
                    <Select placeholder="Indirect Exporter" disabled>
                      <Option value={true}>Yes</Option>
                      <Option value={false}>No</Option>
                    </Select>
                  </Form.Item>
                </Col>

                <Col span={24}>
                  <Form.Item name="indirectExporterCompanies" label="Indirect Exporter Companies">
                    <Input.TextArea placeholder="Indirect Exporter Companies" rows={2} readOnly />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item name="exporter" label="Exporter">
                    <Input placeholder="Exporter" readOnly />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item name="exporterAddress" label="Exporter Address">
                    <Input placeholder="Exporter Address" readOnly />
                  </Form.Item>
                </Col>

                <Col span={24}>
                  <Divider orientation="left" style={{ marginTop: 24, marginBottom: 16 }}>
                    <Typography.Title level={5} style={{ margin: 0 }}>
                      Bill of Materials (BOM)
                    </Typography.Title>
                  </Divider>
                </Col>

                {stockType?.bom && stockType.bom.length > 0 ? (
                  <Col span={24}>
                    <Table
                      size="small"
                      bordered
                      pagination={false}
                      dataSource={stockType.bom}
                      rowKey="id"
                      columns={[
                        {
                          title: 'Product Name',
                          dataIndex: 'name',
                          key: 'name',
                          width: '25%',
                        },
                        {
                          title: 'Description',
                          dataIndex: 'description',
                          key: 'description',
                          width: '35%',
                        },
                        {
                          title: 'Unit',
                          dataIndex: 'defaultUnit',
                          key: 'defaultUnit',
                          width: '10%',
                        },
                        {
                          title: 'Annual Output',
                          dataIndex: 'defaultAnnualOutput',
                          key: 'defaultAnnualOutput',
                          width: '15%',
                        },
                        {
                          title: 'Unit Price FOB',
                          dataIndex: 'unitPriceFOB',
                          key: 'unitPriceFOB',
                          width: '15%',
                        },
                      ]}
                    />
                  </Col>
                ) : (
                  <Col span={24}>
                    <div style={{
                      padding: "20px",
                      textAlign: "center",
                      background: "#787171ff",
                      borderRadius: "4px",
                      color: "#141212ff"
                    }}>
                      No BOM Details Found
                    </div>
                  </Col>
                )}

                <Col span={24}>
                  <Divider orientation="left" style={{ marginTop: 24, marginBottom: 16 }}>
                    <Typography.Title level={5} style={{ margin: 0 }}>
                      Guarantee Requests
                    </Typography.Title>
                  </Divider>
                </Col>

                <Col span={8}>
                  <Form.Item name="requestBankGuarantee" label="Request Bank Guarantee">
                    <Select placeholder="Bank Guarantee" disabled>
                      <Option value={true}>Yes</Option>
                      <Option value={false}>No</Option>
                    </Select>
                  </Form.Item>
                </Col>

                <Col span={8}>
                  <Form.Item name="requestBankAndExporterGuarantee" label="Request Bank and Exporter Guarantee">
                    <Select placeholder="Bank and Exporter Guarantee" disabled>
                      <Option value={true}>Yes</Option>
                      <Option value={false}>No</Option>
                    </Select>
                  </Form.Item>
                </Col>

                <Col span={8}>
                  <Form.Item name="requestExporterGuarantee" label="Request Exporter Guarantee">
                    <Select placeholder="Exporter Guarantee" disabled>
                      <Option value={true}>Yes</Option>
                      <Option value={false}>No</Option>
                    </Select>
                  </Form.Item>
                </Col>

                <Col span={24}>
                  <Divider orientation="left" style={{ marginTop: 24, marginBottom: 16 }}>
                    <Typography.Title level={5} style={{ margin: 0 }}>
                      Approved Guarantees
                    </Typography.Title>
                  </Divider>
                </Col>

                <Col span={8}>
                  <Form.Item name="approvedBankGuarantee" label="Approved Bank Guarantee">
                    <Select placeholder="Bank Guarantee" disabled>
                      <Option value={true}>Yes</Option>
                      <Option value={false}>No</Option>
                    </Select>
                  </Form.Item>
                </Col>

                <Col span={8}>
                  <Form.Item name="approvedBankAndExporterGuarantee" label="Approved Bank and Exporter Guarantee">
                    <Select placeholder="Bank and Exporter Guarantee" disabled>
                      <Option value={true}>Yes</Option>
                      <Option value={false}>No</Option>
                    </Select>
                  </Form.Item>
                </Col>

                <Col span={8}>
                  <Form.Item name="approvedExporterGuarantee" label="Approved Exporter Guarantee">
                    <Select placeholder="Exporter Guarantee" disabled>
                      <Option value={true}>Yes</Option>
                      <Option value={false}>No</Option>
                    </Select>
                  </Form.Item>
                </Col>

                <Col span={24}>
                  <Divider orientation="left" style={{ marginTop: 24, marginBottom: 16 }}>
                    <Typography.Title level={5} style={{ margin: 0 }}>
                      Authorization Details
                    </Typography.Title>
                  </Divider>
                </Col>

                <Col span={8}>
                  <Form.Item name="authorisedBy" label="Authorised By">
                    <Input placeholder="Authorised By" readOnly />
                  </Form.Item>
                </Col>

                <Col span={8}>
                  <Form.Item name="designation" label="Designation">
                    <Input placeholder="Designation" readOnly />
                  </Form.Item>
                </Col>

                <Col span={8}>
                  <Form.Item name="approvedBy" label="Approved By">
                    <Input placeholder="Approved By" readOnly />
                  </Form.Item>
                </Col>

                <Col span={24}>
                  <Divider orientation="left" style={{ marginTop: 24, marginBottom: 16 }}>
                    <Typography.Title level={5} style={{ margin: 0 }}>
                      Additional Information
                    </Typography.Title>
                  </Divider>
                </Col>

                <Col span={8}>
                  <Form.Item name="approvedDate" label="Approved Date">
                    <DatePicker
                      placeholder="Approved Date"
                      format="YYYY-MM-DD"
                      style={{ width: "100%" }}
                      disabled
                    />
                  </Form.Item>
                </Col>

                <Col span={8}>
                  <Form.Item name="validFrom" label="Valid From">
                    <DatePicker 
                      placeholder="Valid From"
                      format="YYYY-MM-DD"
                      style={{ width: "100%" }}
                      disabled
                    />
                  </Form.Item>
                </Col>

                <Col span={8}>
                  <Form.Item name="validTo" label="Valid To">
                    <DatePicker 
                      placeholder="Valid To"
                      format="YYYY-MM-DD"
                      style={{ width: "100%" }}
                      disabled
                    />
                  </Form.Item>
                </Col>

                <Col span={24}>
                  <Form.Item name="comment" label="Comments">
                    <Input.TextArea placeholder="Comments" rows={4} readOnly />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Spin>
        </Page>
      </PageHeader>
    </div>
  );
}

export default Summary;
